import React, { useEffect, useState } from 'react';
import UserLayout from '../components/UserLayout';
import Card, { CardHeader, CardBody } from '../components/Card';
import Button from '../components/Button';
import { CreditCardIcon, DownloadIcon, FilterIcon, ChevronDownIcon, SearchIcon, CheckIcon, RefreshCwIcon, CalendarIcon, DollarSignIcon, BuildingIcon, EyeIcon, ChevronRightIcon, InboxIcon } from 'lucide-react';
// Mock payment data
const mockPayments = [{
  id: 'PAY-2023-001',
  reference: 'REF123456789',
  amount: 15000,
  date: '2023-06-18',
  status: 'successful',
  serviceName: 'Business Registration',
  mdaName: 'Ministry of Commerce and Industry',
  paymentMethod: 'Card',
  receiptUrl: '#'
}, {
  id: 'PAY-2023-002',
  reference: 'REF987654321',
  amount: 5000,
  date: '2023-06-12',
  status: 'successful',
  serviceName: 'Tax Clearance Certificate',
  mdaName: 'Ministry of Finance',
  paymentMethod: 'Bank Transfer',
  receiptUrl: '#'
}, {
  id: 'PAY-2023-003',
  reference: 'REF567891234',
  amount: 25000,
  date: '2023-06-05',
  status: 'pending',
  serviceName: 'Building Permit',
  mdaName: 'Ministry of Lands and Housing',
  paymentMethod: 'Card',
  receiptUrl: null
}, {
  id: 'PAY-2023-004',
  reference: 'REF432156789',
  amount: 10000,
  date: '2023-05-28',
  status: 'failed',
  serviceName: "Driver's License Renewal",
  mdaName: 'Federal Road Safety Commission',
  paymentMethod: 'Card',
  receiptUrl: null,
  failureReason: 'Insufficient funds'
}, {
  id: 'PAY-2023-005',
  reference: 'REF789123456',
  amount: 50000,
  date: '2023-05-20',
  status: 'successful',
  serviceName: 'Certificate of Occupancy',
  mdaName: 'Ministry of Lands and Housing',
  paymentMethod: 'Bank Transfer',
  receiptUrl: '#'
}];
type PaymentStatus = 'all' | 'successful' | 'pending' | 'failed';
type SortOption = 'newest' | 'oldest' | 'amount-high' | 'amount-low';
const PaymentHistory: React.FC = () => {
  const [payments, setPayments] = useState<typeof mockPayments>([]);
  const [filteredPayments, setFilteredPayments] = useState<typeof mockPayments>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus>('all');
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [selectedMda, setSelectedMda] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showMdaDropdown, setShowMdaDropdown] = useState(false);
  const [expandedPayment, setExpandedPayment] = useState<string | null>(null);
  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setPayments(mockPayments);
      setFilteredPayments(mockPayments);
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  // Filter payments based on search term, status filter, and selected MDA
  useEffect(() => {
    let result = [...payments];
    // Apply search filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      result = result.filter(payment => payment.serviceName.toLowerCase().includes(searchTermLower) || payment.id.toLowerCase().includes(searchTermLower) || payment.reference.toLowerCase().includes(searchTermLower) || payment.mdaName.toLowerCase().includes(searchTermLower));
    }
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(payment => payment.status === statusFilter);
    }
    // Apply MDA filter
    if (selectedMda !== 'all') {
      result = result.filter(payment => payment.mdaName === selectedMda);
    }
    // Apply sorting
    result = sortPayments(result, sortOption);
    setFilteredPayments(result);
  }, [payments, searchTerm, statusFilter, selectedMda, sortOption]);
  const sortPayments = (paymentsList: typeof mockPayments, option: SortOption) => {
    const paymentsCopy = [...paymentsList];
    switch (option) {
      case 'newest':
        return paymentsCopy.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      case 'oldest':
        return paymentsCopy.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      case 'amount-high':
        return paymentsCopy.sort((a, b) => b.amount - a.amount);
      case 'amount-low':
        return paymentsCopy.sort((a, b) => a.amount - b.amount);
      default:
        return paymentsCopy;
    }
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  const handleStatusFilter = (status: PaymentStatus) => {
    setStatusFilter(status);
    setShowFilterDropdown(false);
  };
  const handleSort = (option: SortOption) => {
    setSortOption(option);
    setShowSortDropdown(false);
  };
  const handleMdaFilter = (mdaName: string) => {
    setSelectedMda(mdaName);
    setShowMdaDropdown(false);
  };
  const toggleExpandPayment = (paymentId: string) => {
    if (expandedPayment === paymentId) {
      setExpandedPayment(null);
    } else {
      setExpandedPayment(paymentId);
    }
  };
  const refreshPayments = () => {
    setIsLoading(true);
    setTimeout(() => {
      setPayments(mockPayments);
      setIsLoading(false);
    }, 1000);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'successful':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5741">
            <CheckIcon className="h-3 w-3 mr-1" data-id="element-5742" />
            Successful
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-5743">
            <RefreshCwIcon className="h-3 w-3 mr-1" data-id="element-5744" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5745">
            <ChevronDownIcon className="h-3 w-3 mr-1" data-id="element-5746" />
            Failed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5747">
            Unknown
          </span>;
    }
  };
  // Get unique MDAs for filter
  const uniqueMdas = Array.from(new Set(payments.map(payment => payment.mdaName)));
  if (isLoading) {
    return <UserLayout data-id="element-5748">
        <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-5749">
          <div className="max-w-7xl mx-auto flex items-center justify-center h-64" data-id="element-5750">
            <div className="text-center" data-id="element-5751">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" data-id="element-5752"></div>
              <p className="text-gray-600" data-id="element-5753">Loading your payment history...</p>
            </div>
          </div>
        </div>
      </UserLayout>;
  }
  return <UserLayout data-id="element-5754">
      <div className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen" data-id="element-5755">
        <div className="max-w-7xl mx-auto" data-id="element-5756">
          {/* Page Header */}
          <div className="mb-6" data-id="element-5757">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5758">
              Payment History
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-5759">
              View and manage all your payment transactions
            </p>
          </div>
          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow mb-6 p-4" data-id="element-5760">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-5761">
              <div className="flex-grow relative" data-id="element-5762">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5763">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5764" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search by service name, payment ID, or reference number" value={searchTerm} onChange={handleSearch} data-id="element-5765" />
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-5766">
                {/* Status Filter */}
                <div className="relative" data-id="element-5767">
                  <button className="flex items-center text-sm bg-white border border-gray-300 rounded-md px-3 py-2" onClick={() => setShowFilterDropdown(!showFilterDropdown)} data-id="element-5768">
                    <FilterIcon className="h-4 w-4 mr-2" data-id="element-5769" />
                    Status:{' '}
                    {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    <ChevronDownIcon className="h-4 w-4 ml-2" data-id="element-5770" />
                  </button>
                  {showFilterDropdown && <div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-5771">
                      <div className="py-1" data-id="element-5772">
                        {(['all', 'successful', 'pending', 'failed'] as PaymentStatus[]).map(status => <button key={status} className={`flex items-center w-full text-left px-4 py-2 text-sm ${statusFilter === status ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleStatusFilter(status)} data-id="element-5773">
                            {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
                            {statusFilter === status && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5774" />}
                          </button>)}
                      </div>
                    </div>}
                </div>
                {/* MDA Filter */}
                <div className="relative" data-id="element-5775">
                  <button className="flex items-center text-sm bg-white border border-gray-300 rounded-md px-3 py-2" onClick={() => setShowMdaDropdown(!showMdaDropdown)} data-id="element-5776">
                    <BuildingIcon className="h-4 w-4 mr-2" data-id="element-5777" />
                    MDA: {selectedMda === 'all' ? 'All' : selectedMda}
                    <ChevronDownIcon className="h-4 w-4 ml-2" data-id="element-5778" />
                  </button>
                  {showMdaDropdown && <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-5779">
                      <div className="py-1 max-h-60 overflow-y-auto" data-id="element-5780">
                        <button className={`flex items-center w-full text-left px-4 py-2 text-sm ${selectedMda === 'all' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleMdaFilter('all')} data-id="element-5781">
                          All MDAs
                          {selectedMda === 'all' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5782" />}
                        </button>
                        {uniqueMdas.map(mda => <button key={mda} className={`flex items-center w-full text-left px-4 py-2 text-sm ${selectedMda === mda ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleMdaFilter(mda)} data-id="element-5783">
                            {mda}
                            {selectedMda === mda && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5784" />}
                          </button>)}
                      </div>
                    </div>}
                </div>
                {/* Sort Options */}
                <div className="relative" data-id="element-5785">
                  <button className="flex items-center text-sm bg-white border border-gray-300 rounded-md px-3 py-2" onClick={() => setShowSortDropdown(!showSortDropdown)} data-id="element-5786">
                    <div className="h-4 w-4 mr-2" data-id="element-5787" />
                    Sort by
                    <ChevronDownIcon className="h-4 w-4 ml-2" data-id="element-5788" />
                  </button>
                  {showSortDropdown && <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-5789">
                      <div className="py-1" data-id="element-5790">
                        <button className={`flex items-center w-full text-left px-4 py-2 text-sm ${sortOption === 'newest' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleSort('newest')} data-id="element-5791">
                          Newest First
                          {sortOption === 'newest' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5792" />}
                        </button>
                        <button className={`flex items-center w-full text-left px-4 py-2 text-sm ${sortOption === 'oldest' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleSort('oldest')} data-id="element-5793">
                          Oldest First
                          {sortOption === 'oldest' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5794" />}
                        </button>
                        <button className={`flex items-center w-full text-left px-4 py-2 text-sm ${sortOption === 'amount-high' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleSort('amount-high')} data-id="element-5795">
                          Amount: High to Low
                          {sortOption === 'amount-high' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5796" />}
                        </button>
                        <button className={`flex items-center w-full text-left px-4 py-2 text-sm ${sortOption === 'amount-low' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleSort('amount-low')} data-id="element-5797">
                          Amount: Low to High
                          {sortOption === 'amount-low' && <CheckIcon className="h-4 w-4 ml-auto" data-id="element-5798" />}
                        </button>
                      </div>
                    </div>}
                </div>
                {/* Refresh Button */}
                <button className="flex items-center text-sm bg-white border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-50" onClick={refreshPayments} data-id="element-5799">
                  <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-5800" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
          {/* Results Summary */}
          <div className="mb-4 flex items-center justify-between" data-id="element-5801">
            <div className="text-sm text-gray-600" data-id="element-5802">
              Showing {filteredPayments.length} of {payments.length} payments
            </div>
          </div>
          {/* Payments List */}
          {filteredPayments.length > 0 ? <div className="space-y-4" data-id="element-5803">
              {filteredPayments.map(payment => <Card key={payment.id} data-id="element-5804">
                  <CardBody className="p-0" data-id="element-5805">
                    {/* Main Payment Info */}
                    <div className="p-4 sm:p-6" data-id="element-5806">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4" data-id="element-5807">
                        <div className="flex-1" data-id="element-5808">
                          <div className="flex items-start" data-id="element-5809">
                            <div className="mr-3" data-id="element-5810">
                              <CreditCardIcon className="h-6 w-6 text-gray-400" data-id="element-5811" />
                            </div>
                            <div data-id="element-5812">
                              <h3 className="text-lg font-medium text-gray-900" data-id="element-5813">
                                {payment.serviceName}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2 mt-1" data-id="element-5814">
                                {getStatusBadge(payment.status)}
                                <span className="text-xs text-gray-500" data-id="element-5815">
                                  <CalendarIcon className="h-3 w-3 inline mr-1" data-id="element-5816" />
                                  Date:{' '}
                                  {new Date(payment.date).toLocaleDateString()}
                                </span>
                                <span className="text-xs text-gray-500" data-id="element-5817">
                                  <BuildingIcon className="h-3 w-3 inline mr-1" data-id="element-5818" />
                                  {payment.mdaName}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 sm:mt-0 flex items-center gap-2" data-id="element-5819">
                          {payment.status === 'successful' && payment.receiptUrl && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(payment.receiptUrl || '#', '_blank')} data-id="element-5820">
                                <DownloadIcon className="h-3 w-3 mr-1" data-id="element-5821" />
                                Receipt
                              </Button>}
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => toggleExpandPayment(payment.id)} data-id="element-5822">
                            <EyeIcon className="h-3 w-3 mr-1" data-id="element-5823" />
                            Details
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row justify-between" data-id="element-5824">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm" data-id="element-5825">
                          <div data-id="element-5826">
                            <span className="text-gray-500" data-id="element-5827">Payment ID:</span>
                            <span className="ml-1 font-medium" data-id="element-5828">
                              {payment.id}
                            </span>
                          </div>
                          <div data-id="element-5829">
                            <span className="text-gray-500" data-id="element-5830">Reference:</span>
                            <span className="ml-1 font-medium" data-id="element-5831">
                              {payment.reference}
                            </span>
                          </div>
                          <div data-id="element-5832">
                            <span className="text-gray-500" data-id="element-5833">Amount:</span>
                            <span className="ml-1 font-medium text-green-600" data-id="element-5834">
                              ₦{payment.amount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Failed Payment Notice */}
                      {payment.status === 'failed' && payment.failureReason && <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4" data-id="element-5835">
                          <div className="flex" data-id="element-5836">
                            <div className="flex-shrink-0" data-id="element-5837">
                              <ChevronDownIcon className="h-5 w-5 text-red-400" data-id="element-5838" />
                            </div>
                            <div className="ml-3" data-id="element-5839">
                              <h3 className="text-sm font-medium text-red-800" data-id="element-5840">
                                Payment Failed
                              </h3>
                              <div className="mt-2 text-sm text-red-700" data-id="element-5841">
                                <p data-id="element-5842">Reason: {payment.failureReason}</p>
                              </div>
                              <div className="mt-3" data-id="element-5843">
                                <Button variant="primary" size="sm" data-id="element-5844">
                                  Try Again
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>}
                    </div>
                    {/* Expanded Details */}
                    {expandedPayment === payment.id && <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50" data-id="element-5845">
                        <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-5846">
                          Payment Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5847">
                          <div data-id="element-5848">
                            <h5 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5849">
                              Transaction Information
                            </h5>
                            <div className="bg-white p-4 rounded-md border border-gray-200" data-id="element-5850">
                              <dl className="space-y-2 text-sm" data-id="element-5851">
                                <div className="flex justify-between" data-id="element-5852">
                                  <dt className="text-gray-500" data-id="element-5853">
                                    Payment Method:
                                  </dt>
                                  <dd className="font-medium" data-id="element-5854">
                                    {payment.paymentMethod}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5855">
                                  <dt className="text-gray-500" data-id="element-5856">
                                    Transaction Date:
                                  </dt>
                                  <dd className="font-medium" data-id="element-5857">
                                    {new Date(payment.date).toLocaleDateString()}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5858">
                                  <dt className="text-gray-500" data-id="element-5859">
                                    Transaction Time:
                                  </dt>
                                  <dd className="font-medium" data-id="element-5860">
                                    {new Date(payment.date).toLocaleTimeString()}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5861">
                                  <dt className="text-gray-500" data-id="element-5862">
                                    Reference Number:
                                  </dt>
                                  <dd className="font-medium" data-id="element-5863">
                                    {payment.reference}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5864">
                                  <dt className="text-gray-500" data-id="element-5865">Status:</dt>
                                  <dd className="font-medium" data-id="element-5866">
                                    {getStatusBadge(payment.status)}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                          <div data-id="element-5867">
                            <h5 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5868">
                              Service Information
                            </h5>
                            <div className="bg-white p-4 rounded-md border border-gray-200" data-id="element-5869">
                              <dl className="space-y-2 text-sm" data-id="element-5870">
                                <div className="flex justify-between" data-id="element-5871">
                                  <dt className="text-gray-500" data-id="element-5872">Service:</dt>
                                  <dd className="font-medium" data-id="element-5873">
                                    {payment.serviceName}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5874">
                                  <dt className="text-gray-500" data-id="element-5875">MDA:</dt>
                                  <dd className="font-medium" data-id="element-5876">
                                    {payment.mdaName}
                                  </dd>
                                </div>
                                <div className="flex justify-between" data-id="element-5877">
                                  <dt className="text-gray-500" data-id="element-5878">Amount:</dt>
                                  <dd className="font-medium text-green-600" data-id="element-5879">
                                    ₦{payment.amount.toLocaleString()}
                                  </dd>
                                </div>
                                {payment.status === 'successful' && <div className="pt-2" data-id="element-5880">
                                    <Button variant="primary" size="sm" className="w-full" onClick={() => window.open(payment.receiptUrl || '#', '_blank')} data-id="element-5881">
                                      <DownloadIcon className="h-4 w-4 mr-2" data-id="element-5882" />
                                      Download Receipt
                                    </Button>
                                  </div>}
                              </dl>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end" data-id="element-5883">
                          <Button variant="outline" size="sm" onClick={() => toggleExpandPayment(payment.id)} data-id="element-5884">
                            Hide Details
                          </Button>
                        </div>
                      </div>}
                  </CardBody>
                </Card>)}
            </div> : <div className="bg-white rounded-lg shadow py-12 px-4 text-center" data-id="element-5885">
              <div className="flex justify-center" data-id="element-5886">
                <InboxIcon className="h-12 w-12 text-gray-400" data-id="element-5887" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-5888">
                No payment records found
              </h3>
              <p className="mt-2 text-sm text-gray-500" data-id="element-5889">
                {searchTerm || statusFilter !== 'all' || selectedMda !== 'all' ? 'Try adjusting your filters to see more results.' : "You haven't made any payments yet."}
              </p>
            </div>}
        </div>
      </div>
    </UserLayout>;
};
export default PaymentHistory;